import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Quote, Award, Users, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import TestimonialWall from '../components/TestimonialWall';
import SEOHead from '../components/SEOHead';
import { apiFetch } from '../lib/apiClient';
import { scrollToSection } from '../utils/smoothScroll';
import useSectionProgress from '../hooks/useSectionProgress';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback testimonials if API returns empty
const fallbackTestimonials = [
  { id: 'f1', name: 'Marco Bianchi', role: 'CEO, TechStart Srl', text: 'Nicola ha completamente trasformato il nostro approccio al mercato. In 12 mesi abbiamo triplicato il fatturato.', rating: 5 },
  { id: 'f2', name: 'Laura Verdi', role: 'Founder, GreenLife', text: 'Pensavo di sapere cosa stessi facendo con il marketing. Nicola mi ha aperto gli occhi su quanto stavo lasciando sul tavolo.', rating: 5 },
  { id: 'f3', name: 'Alessandro Rossi', role: 'Managing Director, Rossi Group', text: 'Finalmente un consulente che parla di numeri, non di vanity metrics. Ogni euro investito è tracciabile.', rating: 5 },
  { id: 'f4', name: 'Giulia Neri', role: 'CMO, InnovateTech', text: 'Il metodo D.O.M.I.N.A. ha dato struttura a tutto il nostro reparto marketing. Risultati incredibili.', rating: 5 },
  { id: 'f5', name: 'Roberto Marino', role: 'Owner, Marino & Partners', text: 'La consulenza di Nicola vale 10 volte quello che costa. ROI straordinario già dal primo trimestre.', rating: 5 },
  { id: 'f6', name: 'Francesca Costa', role: 'Direttore Commerciale, SalesForce Italia', text: 'Abbiamo finalmente una strategia chiara che tutto il team può seguire. Nicola è un vero professionista.', rating: 5 },
  { id: 'f7', name: 'Paolo Ferretti', role: 'CEO, Digital Solutions', text: 'Il nostro fatturato è cresciuto del 180% in 18 mesi grazie alla strategia sviluppata con Nicola.', rating: 5 },
  { id: 'f8', name: 'Chiara Romano', role: 'Founder, EcoStyle', text: 'Nicola non è il solito consulente. Porta risultati concreti e misurabili. Lo consiglio a tutti.', rating: 5 },
  { id: 'f9', name: 'Davide Conti', role: 'Partner, Conti & Associati', text: 'Un approccio strategico che ha cambiato il modo in cui pensiamo al marketing. Eccezionale.', rating: 5 },
  { id: 'f10', name: 'Elena Rizzo', role: 'Marketing Manager, Luxe Brand', text: 'La chiarezza e la professionalità di Nicola sono rare. Ogni sessione porta valore tangibile.', rating: 5 },
  { id: 'f11', name: 'Simone Greco', role: 'CEO, StartupHub', text: 'Ho lavorato con molti consulenti, ma Nicola è su un altro livello. Strategia pura, zero fuffa.', rating: 5 },
  { id: 'f12', name: 'Valentina Longo', role: 'Founder, BeautyLab', text: 'Il nostro brand è cresciuto esponenzialmente grazie alla visione strategica di Nicola.', rating: 5 },
];

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ChiSonoPage = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const parallaxElements = useRef([]);
  const timelineFillRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const timelineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Parallax mouse effect - using refs to avoid re-renders
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      mousePositionRef.current = { x, y };
      
      // Update parallax elements directly via DOM
      parallaxElements.current.forEach((el, i) => {
        if (el) {
          const multiplier = i === 1 ? -1 : i === 2 ? 0.5 : 1;
          el.style.transform = `translate(${x * multiplier}px, ${y * multiplier}px)`;
        }
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/testimonials`);
        if (response.ok) {
          const data = await response.json();
          // Use fallback if API returns empty
          setTestimonials(data.length > 0 ? data : fallbackTestimonials);
        } else {
          setTestimonials(fallbackTestimonials);
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials(fallbackTestimonials);
      }
    };
    fetchTestimonials();
  }, []);

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  const highlights = [
    'Ho iniziato la mia carriera nel marketing oltre 14 anni fa, quando il "digitale" era ancora un territorio inesplorato.',
    'Non sapevo esattamente dove sarei arrivato. Sapevo solo che volevo aiutare le aziende a crescere in modo misurabile.',
    'Ho lavorato con aziende selezionate generando più di €2.5M di revenue attraverso strategie di marketing efficaci.',
    'Oggi lavoro solo con aziende selezionate che condividono la mia visione: il marketing è un investimento, non un costo.',
  ];

  const storyBlocks = [
    {
      year: '2011',
      title: 'L\'inizio del viaggio',
      text: 'Ho mosso i primi passi nel marketing digitale come consulente freelance. All\'epoca erano pochi gli esperti del settore in Italia, e questo mi ha permesso di costruire una solida base di esperienza lavorando con i primi clienti B2B.',
    },
    {
      year: '2014',
      title: 'La prima agenzia',
      text: 'Ho fondato la mia prima agenzia di marketing. In soli 3 anni siamo arrivati a gestire 25 clienti attivi, costruendo un team di professionisti appassionati e competenti.',
    },
    {
      year: '2018',
      title: 'La specializzazione',
      text: 'Ho deciso di specializzarmi in strategia di marketing per PMI ambiziose. È in questo periodo che ho sviluppato il metodo D.O.M.I.N.A., un framework proprietario che ancora oggi guida ogni mia consulenza.',
    },
    {
      year: '2022',
      title: 'Consulenza strategica pura',
      text: 'Ho fatto una scelta radicale: abbandonare l\'operatività per concentrarmi esclusivamente sulla consulenza strategica. Oggi lavoro solo con aziende che fatturano almeno €100K e che sono pronte a investire seriamente nel loro marketing.',
    },
    {
      year: 'Oggi',
      title: 'Il presente',
      text: 'Lavoro con aziende selezionate in tutta Italia. Il mio focus è uno solo: generare risultati misurabili e duraturi per chi si affida a me.',
    },
  ];

  const activeStory = useSectionProgress(timelineRef, timelineFillRef, storyBlocks.length);

  const values = [
    {
      number: '01',
      title: 'Strategia prima di tutto',
      description: 'Non spendo un euro in advertising prima di avere una strategia chiara. Mai tattiche senza direzione.',
      icon: Award,
    },
    {
      number: '02',
      title: 'Dati, non opinioni',
      description: 'Ogni decisione deve essere basata su numeri. Le sensazioni sono utili, ma i dati sono essenziali.',
      icon: TrendingUp,
    },
    {
      number: '03',
      title: 'Risultati misurabili',
      description: 'Non lavoro per fatturare ore. Lavoro per generare risultati concreti e verificabili per i miei clienti.',
      icon: Users,
    },
  ];

  const stats = [
    { value: '14+', label: 'Anni di esperienza', delay: 0 },
    { value: '€2.5M+', label: 'Revenue generata', delay: 100 },
    { value: '4', label: 'Clienti alla volta', delay: 200 },
    { value: '89%', label: 'Tasso di successo', delay: 300 },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEOHead page="chiSono" />
      <Navbar />
      
      {/* Hero Section - Full Screen like Home */}
      <section id="hero" className="relative min-h-screen bg-[#0b114e] overflow-hidden">
        {/* Background Image - Extended on mobile to avoid edge line */}
        <div 
          className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
          style={isMobile ? { left: '-20%', right: 'auto', width: '120%' } : {}}
        >
          <div 
            className="absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url('/media/nicola-hero.webp')`,
              backgroundPosition: isMobile ? '-30% top' : 'center top',
            }}
          />
          {/* Subtle blue tint overlay */}
          <div className="absolute inset-0 bg-[#0b114e]/5" />
          {/* Gradient inside image container */}
          <div className={`absolute inset-0 ${
            isMobile 
              ? 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/80 via-40% to-transparent to-70%' 
              : 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/70 via-40% to-transparent to-80%'
          }`} />
        </div>

        {/* Content - Centered */}
        <div className="relative min-h-screen flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="max-w-2xl">
              {/* Badge */}
              <div 
                className={`hidden sm:inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: '200ms' }}
              >
                <span className="w-2 h-2 bg-[#d4883a] rounded-full animate-pulse" />
                <span className="text-white/70 text-sm tracking-widest">Chi sono</span>
              </div>

              {/* Main Heading */}
              <h1 
                className={`mt-[4.5rem] sm:mt-0 text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '400ms' }}
              >
                Creo strategie per{' '}
                <span className="text-[#d4883a] relative">
                  dominare
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path 
                      d="M0 4C50 4 50 4 100 4C150 4 150 4 200 4" 
                      stroke="#d4883a" 
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      style={{ 
                        strokeDasharray: 200, 
                        strokeDashoffset: isLoaded ? 0 : 200,
                        transitionDelay: '1200ms'
                      }}
                    />
                  </svg>
                </span>{' '}
                il tuo mercato.
              </h1>

              {/* Subtitle */}
              <p 
                className={`text-white/60 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 leading-relaxed max-w-xl transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '600ms' }}
              >
                Consulente di Marketing Strategico per imprenditori ambiziosi. 
                Ho trasformato il marketing da costo a investimento per <strong className="text-white">aziende selezionate</strong>.
              </p>
              
              {/* Check indicators - Compact single row on mobile */}
              <div 
                className={`flex items-center gap-2 mb-6 sm:hidden text-white/50 text-xs transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '700ms' }}
              >
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>14+ anni</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>€2.5M+ generati</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Max 4 clienti</span>
              </div>

              {/* CTA Button with hover animation */}
              <div 
                className={`mb-8 sm:mb-12 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '800ms' }}
              >
                <button
                  onClick={scrollToContact}
                  data-testid="chi-sono-cta-button"
                  className="group bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all flex items-center justify-center gap-2 text-base sm:text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
                >
                  Parliamo del tuo progetto
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              
              {/* Stats row - Desktop only */}
              <div 
                className={`hidden sm:flex flex-wrap gap-8 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '1000ms' }}
              >
                {stats.slice(0, 3).map((stat, index) => (
                  <div 
                    key={index}
                    className={`transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${1100 + index * 100}ms` }}
                  >
                    <p className="text-3xl lg:text-4xl font-bold text-[#d4883a]">{stat.value}</p>
                    <p className="text-white/60 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div 
          className={`absolute bottom-8 left-6 lg:left-12 max-w-lg hidden sm:block transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '1200ms' }}
        >
          <div className="border-l-2 border-[#d4883a]/50 pl-4">
            <p className="text-white/40 italic text-sm">
              "La strategia senza esecuzione è un'illusione.<br />
              L'esecuzione senza strategia è caos."
            </p>
          </div>
        </div>
      </section>

      {/* Chi Sono Intro Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f9fa] to-transparent" />
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left Column - Title */}
            <AnimatedSection>
              <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Chi sono</p>
              <h2 
                className="text-3xl lg:text-5xl text-[#0b114e] leading-tight"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Mente strategica,{' '}
                <span className="text-[#d4883a]">approccio pragmatico,</span>{' '}
                risultati misurabili.
              </h2>
            </AnimatedSection>
            
            {/* Right Column - Highlights */}
            <div className="space-y-6">
              {highlights.map((text, index) => (
                <AnimatedSection key={index} delay={index * 150}>
                  <div className="flex gap-4 items-start group cursor-default">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#d4883a]/10 rounded-full flex items-center justify-center group-hover:bg-[#d4883a] group-hover:scale-110 transition-all duration-300">
                      <CheckCircle className="w-5 h-5 text-[#d4883a] group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-gray-600 text-lg leading-relaxed group-hover:text-gray-800 transition-colors">{text}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner Section */}
      <section className="py-16 bg-[#0b114e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#d4883a] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedSection key={index} delay={index * 100} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-[#d4883a] mb-2">{stat.value}</p>
                <p className="text-white/70 text-sm lg:text-base">{stat.label}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Story Timeline Section */}
      <section className="py-20 lg:py-32 bg-[#f8f9fa] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#d4883a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0b114e]/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-16 lg:mb-24">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Il mio percorso</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              La mia storia in <span className="text-[#d4883a]">breve</span>
            </h2>
          </AnimatedSection>
          
          {/* Timeline */}
          <div className="relative" ref={timelineRef}>
            {/* Timeline line - background (gray) */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-[#0b114e]/10 -translate-x-1/2 rounded-full" />
            
            {/* Timeline line - filled (animated with scroll) */}
            <div 
              ref={timelineFillRef}
              className="hidden lg:block absolute left-1/2 top-0 h-full w-1 bg-gradient-to-b from-[#d4883a] to-[#d4883a] rounded-full origin-top will-change-transform"
              style={{ 
                transform: 'translateX(-50%) scaleY(0)',
                boxShadow: '0 0 10px rgba(212, 136, 58, 0.5)',
              }}
            />
            
            <div className="space-y-12 lg:space-y-0">
              {storyBlocks.map((block, index) => {
                const isActive = index <= activeStory;
                
                return (
                  <AnimatedSection 
                    key={index}
                    delay={index * 100}
                    className={`relative lg:flex lg:items-center lg:min-h-[200px] ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                  >
                    {/* Content */}
                    <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16 lg:text-left'}`}>
                      <div 
                        className={`bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 ${index % 2 === 0 ? 'lg:mr-8' : 'lg:ml-8'} ${isActive ? 'ring-2 ring-[#d4883a]/20' : ''}`}
                      >
                        <span className="text-[#d4883a] font-bold text-lg">{block.year}</span>
                        <h3 
                          className="text-xl lg:text-2xl font-bold text-[#0b114e] mt-2 mb-4"
                          style={{ fontFamily: 'Georgia, serif' }}
                        >
                          {block.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {block.text}
                        </p>
                      </div>
                    </div>
                    
                    {/* Timeline dot - desktop only */}
                    <div 
                      className={`hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-16 h-16 rounded-full items-center justify-center shadow-xl z-10 transition-all duration-500 ${
                        isActive 
                          ? 'bg-[#d4883a] scale-110' 
                          : 'bg-[#0b114e]'
                      }`}
                      style={isActive ? { boxShadow: '0 0 20px rgba(212, 136, 58, 0.5)' } : {}}
                    >
                      <span className="text-white font-bold text-sm">{block.year}</span>
                    </div>
                    
                    {/* Empty space for the other side */}
                    <div className="hidden lg:block lg:w-1/2" />
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Quote Section */}
      <section className="py-20 lg:py-32 bg-[#0b114e] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4883a]/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        
        <AnimatedSection className="max-w-[900px] mx-auto px-6 lg:px-12 text-center relative">
          <Quote className="w-16 h-16 text-[#d4883a]/30 mx-auto mb-8" />
          <p 
            className="text-2xl lg:text-4xl text-white leading-relaxed mb-8"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Il marketing non è un costo. È l'investimento con il ROI più alto che puoi fare,{' '}
            <span className="text-[#d4883a]">se sai cosa stai facendo.</span>
          </p>
          <p className="text-[#d4883a] font-semibold text-lg">— Nicola Scerni</p>
        </AnimatedSection>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-1/3 h-96 bg-gradient-to-r from-[#f8f9fa] to-transparent" />
        
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">I miei valori</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e] mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              I principi che guidano{' '}
              <span className="text-[#d4883a]">ogni collaborazione</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Se non condividi questi valori, probabilmente non siamo fatti per lavorare insieme.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <AnimatedSection key={index} delay={index * 150}>
                <div 
                  className="relative bg-[#f8f9fa] rounded-2xl p-8 lg:p-10 group hover:bg-[#0b114e] transition-all duration-500 cursor-pointer overflow-hidden h-full"
                >
                  
                  {/* Large number background */}
                  <span className="absolute top-4 right-4 text-7xl lg:text-8xl font-bold text-[#d4883a]/10 group-hover:text-[#d4883a]/20 transition-colors z-0">
                    {value.number}
                  </span>
                  
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="w-14 h-14 bg-[#d4883a]/10 group-hover:bg-[#d4883a]/20 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110">
                      <value.icon className="w-7 h-7 text-[#d4883a]" />
                    </div>
                    
                    <h3 
                      className="text-xl lg:text-2xl font-bold text-[#0b114e] group-hover:text-white mb-4 transition-colors"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {value.title}
                    </h3>
                    <p className="text-gray-600 group-hover:text-white/70 transition-colors leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#f8f9fa] relative overflow-hidden">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
            <AnimatedSection className="text-center mb-12">
              <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Testimonianze</p>
              <h2 
                className="text-3xl lg:text-5xl text-[#0b114e] mb-4"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Cosa dicono <span className="text-[#d4883a]">di me</span>
              </h2>
            </AnimatedSection>
            
            <TestimonialWall testimonials={testimonials} variant="light" />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f9fa] to-transparent" />
        <div className="absolute top-1/4 right-10 w-32 h-32 border-2 border-[#d4883a]/20 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
        <div className="absolute bottom-1/4 left-10 w-24 h-24 border-2 border-[#0b114e]/10 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }} />
        
        <AnimatedSection className="max-w-[800px] mx-auto px-6 lg:px-12 text-center relative">
          <h2 
            className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Vuoi sapere se posso{' '}
            <span className="text-[#d4883a]">aiutarti</span>?
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
            Prenota una consulenza gratuita di 30 minuti. 
            Analizzeremo insieme la tua situazione e capirai se sono la persona giusta per te.
          </p>
          <button
            onClick={scrollToContact}
            data-testid="chi-sono-cta-final-button"
            className="group inline-flex items-center gap-3 bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-10 py-5 rounded-full transition-all text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
          >
            Prenota una consulenza gratuita
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </button>
        </AnimatedSection>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ChiSonoPage;
